/**
 * Single source of Aura API base URLs — shared by Aura OS + Prometheus Lab.
 * Sync into prometheus-lab-ai/shared via scripts/sync_shared_to_prometheus.*
 */
export const LOCAL_API_BASE = "http://localhost:8001";
export const RAILWAY_API_BASE = "https://web-production-914b8.up.railway.app";

const warned = new Set<string>();
function warnOnce(message: string) {
  if (warned.has(message)) return;
  warned.add(message);
  console.warn(`[apiUrls] ${message}`);
}

/**
 * Turns a configured API base into an absolute URL, or null when unusable.
 * A host without scheme ("api.example.com") used to be fetched as a *relative* path on the
 * frontend's own domain (e.g. pages.dev/api.example.com/api/auth/login → 405), breaking login.
 */
export function normalizeApiBase(raw?: string | null): string | null {
  const value = (raw || "").trim().replace(/\/+$/, "");
  if (!value) return null;
  if (/^https?:\/\/[^/\s]+/i.test(value)) return value;
  if (/^(localhost|127\.0\.0\.1)(:\d+)?(\/\S*)?$/i.test(value)) {
    warnOnce(`VITE_API_URL "${value}" has no scheme; using http://${value}`);
    return `http://${value}`;
  }
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?(\/\S*)?$/i.test(value)) {
    warnOnce(`VITE_API_URL "${value}" has no scheme; using https://${value}`);
    return `https://${value}`;
  }
  warnOnce(`VITE_API_URL "${value}" is not a URL; falling back to ${RAILWAY_API_BASE}`);
  return null;
}

export function resolveApiBase(viteApiUrl?: string | null): string {
  return normalizeApiBase(viteApiUrl) ?? RAILWAY_API_BASE;
}
